import React, {useEffect, useState} from "react";
import {LinearProgress} from '@material-ui/core';
import Resource from "../../crud/resource";
import BarChart from "recharts/lib/chart/BarChart";
import CartesianGrid from "recharts/lib/cartesian/CartesianGrid";
import XAxis from "recharts/lib/cartesian/XAxis";
import YAxis from "recharts/lib/cartesian/YAxis";
import Tooltip from "recharts/lib/component/Tooltip";
import Legend from "recharts/lib/component/Legend";
import Bar from "recharts/lib/cartesian/Bar";
import ResponsiveContainer from "recharts/lib/component/ResponsiveContainer";
import Translate, {getAuthorizedCountries, getDataFromResponse} from "../../utils/utils";
import Swal from "sweetalert2";
import {useIntl} from "react-intl";
import {useSelector} from "react-redux";
import {isString} from "formik";

export default function Dashboard() {
    const [isLoading, setIsLoading] = useState(true);
    const [stats, setStats] = useState({});
    const [countries, setCountries] = useState([]);
    const [countryId, setCountryId] = useState(null);
    const [selectedSummary, setSelectedSummary] = useState('week');
    const intl = useIntl();
    const currentUser = useSelector(store => store.auth.user);
    const authorizedCountries = getAuthorizedCountries(countries, currentUser);


    useEffect(() => {
        const resource = new Resource('dashboard');
        setIsLoading(true);
        resource.index({country_id: countryId})
            .then(response => {
                const {data: {data = {data: []}}} = response;
                setStats(data);
                setIsLoading(false);
            }).catch(() => {
            setIsLoading(false);
            Swal.fire({
                icon: 'error',
                text: 'Failed loading stats'
            });
        });
    }, [countryId]);

    useEffect(() => {
        const resource = new Resource('country');
        resource.index({perPage: 350})
            .then(response => {
                const {data: countries} = getDataFromResponse(response);
                setCountries(countries);
                const authorizedCountries = getAuthorizedCountries(countries, currentUser);
                const countryId = authorizedCountries.length > 1 ? null : getFirstCountry(authorizedCountries)
                setCountryId(countryId)
                setIsLoading(false);
            }).catch(err => {
            setIsLoading(false);
        });

    }, [currentUser]);

    let inspections_label = '';
    let boxes_inspected_label = '';

    const getGraphData = () => {

        const inspections_summary = stats?.summary || {};
        const boxes_inspected_summary = stats?.boxes_inspected?.summary || {};
        const inspections = inspections_summary[selectedSummary] || [];
        const boxes_inspected = boxes_inspected_summary[selectedSummary] || [];
        const data = [];

        inspections.forEach((inspection, key) => {
            inspections_label = inspection.key;
            boxes_inspected_label = boxes_inspected[key]['key'];
            const obj = {};
            obj[inspections_label] = inspection.inspections;
            obj[boxes_inspected_label] = boxes_inspected[key]['boxes_inspected'];
            obj['date'] = inspection.date;
            data.push(obj);
        });

        return data;
    };

    const formatter = (number) => {
        if (isString(number)) return number;

        return new Intl.NumberFormat().format(number);
    }

    const getFirstCountry = (authorizedCountries) => {
        const countries = [...authorizedCountries]
        countries.sort();
        const country = countries.shift();
        return country ? country.id : ''
    }

    return (
        <>
            {isLoading && <LinearProgress color="secondary" className={"mb-1"}/>}
            <div className="row">
                <div className="select_country col-lg-12 mb-2">
                    <select name="country_id" className={"form-control"} onChange={(e) => {
                        setCountryId(e.target.value)
                    }} disabled={authorizedCountries.length <= 1}>

                        {authorizedCountries.length > 1 &&
                            <option value={null}
                                    defaultValue={true}>{intl.formatMessage({id: "DASHBOARD.INSPECTIONS.ALL_COUNTRIES"})}</option>}

                        {authorizedCountries.map(country => {
                            return (<option key={country.id} value={country.id}>{country.name}</option>)
                        })}
                    </select>
                </div>
                <div className="col-xl-12 mb-4">
                    <div className="row">
                        <div className="col-lg-3 mb-3">
                            <div className="stat">
                                <div className='count row kt-space-between'>
                                    <div>{isLoading ? '--' : formatter(stats?.month || 0)}</div>
                                    <div>{isLoading ? '--' : formatter(stats?.boxes_inspected?.month || 0)}</div>
                                </div>
                                <div className='period'>
                                    <Translate id="DASHBOARD.INSPECTIONS.THIS_MONTH"/>
                                </div>
                            </div>
                        </div>

                        <div className="col-lg-3 mb-3">
                            <div className="stat">
                                <div className='count row kt-space-between'>
                                    <div>{isLoading ? '--' : formatter(stats?.week || 0)}</div>
                                    <div>{isLoading ? '--' : formatter(stats?.boxes_inspected?.week || 0)}</div>
                                </div>
                                <div className='period'>
                                    <Translate id="DASHBOARD.INSPECTIONS.THIS_WEEK"/>
                                </div>
                            </div>
                        </div>

                        <div className="col-lg-3 mb-3">
                            <div className="stat">
                                <div className='count row kt-space-between'>
                                    <div>{isLoading ? '--' : formatter(stats?.today || 0)}</div>
                                    <div>{isLoading ? '--' : formatter(stats?.boxes_inspected?.today || 0)}</div>
                                </div>
                                <div className='period'>
                                    <Translate id="DASHBOARD.INSPECTIONS.TODAY"/>
                                </div>
                            </div>
                        </div>

                        <div className="col-lg-3 mb-3">
                            <div className="stat">
                                <div
                                    className='count row kt-space-between'>{isLoading ? '--' : formatter(stats?.unsent || 0)}</div>
                                <div className='period'>
                                    <Translate id="DASHBOARD.INSPECTIONS.UNSENT"/>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>

                <div className="col-xl-12 bg-white">
                    <div className="chart-controls p-3 mb-2">
                        <h5 className="d-inline-block">{
                            intl.formatMessage({id: "DASHBOARD.INSPECTIONS.SUMMARY"})
                        }</h5>
                        <select className="pull-right d-inline-block p-1"
                                onChange={(e) => setSelectedSummary(e.target.value)}>
                            <option value='week'>
                                {intl.formatMessage({id: "DASHBOARD.INSPECTIONS.WEEK"})}
                            </option>
                            <option value='month'>
                                {intl.formatMessage({id: "DASHBOARD.INSPECTIONS.MONTH"})}
                            </option>
                        </select>
                    </div>
                    <ResponsiveContainer width={'100%'} height={500}>
                        <BarChart barSize={12} width={1250} data={getGraphData()}
                                  margin={{top: 5, right: 30, left: 20, bottom: 5}}>
                            <CartesianGrid strokeDasharray="0 0"/>
                            <XAxis dataKey="date"/>
                            <YAxis label={{
                                value: intl.formatMessage({id: "DASHBOARD.INSPECTIONS.AXIS.Y"}),
                                angle: -90,
                                position: 'insideLeft',
                                textAnchor: 'middle',
                                offset: -15,
                            }}/>
                            <Tooltip/>
                            <Legend/>
                            <Bar dataKey={inspections_label} fill="#646c9a"/>
                            <Bar dataKey={boxes_inspected_label} fill="#c32e95"/>
                        </BarChart>

                    </ResponsiveContainer>
                </div>
            </div>
        </>
    );
}
